import React from "react";
import { Link } from "react-router-dom";
import { MarketingLayout } from "../../components/marketing/MarketingLayout";
import { TwoCol } from "../../components/marketing/TwoCol";

const NAME_ROWS = [
  { code: "CR.", name: "Compute Residency.", desc: "Compute and data stay in-country, with no dependency on a hyperscaler abroad." },
  { code: "OW.", name: "Ownership Without Walls.", desc: "Full control for the client, with no restricted interfaces and no vendor gatekeeping." },
  { code: "TH.", name: "Thinking Locally, Trained Locally.", desc: "Specialist models built for the languages, regulations, and workflows of the market they serve, rather than general models applied from outside." },
  { code: "ER.", name: "Enterprise Sovereignty.", desc: "A moat the customer builds and owns, not one they rent." },
];

const VALUES = [
  { name: "Curiosity.", desc: "We take problems seriously enough to look under them. We ask the follow-up question in a room where nobody else is asking it." },
  { name: "Ownership.", desc: "When a piece of work is ours, it is ours end to end. We do not hand off, we do not blame the room next door, we finish it." },
  { name: "Responsibility.", desc: "The people who build the deployment sit with the people who run it. We answer for the systems we ship, in the environments they run in." },
  { name: "Elevation.", desc: "Every deployment must raise the ceiling on what a person can do. If a design displaces more work than it elevates, we redesign it." },
];

export default function About() {
  return (
    <MarketingLayout
      active="about"
      title="About Crowther. Sovereign intelligence, owned in-country."
      description="Crowther builds and owns the intelligence stack that regulated enterprises run on, so that their intelligence, their data, and their infrastructure are owned where they operate."
    >
      {/* Hero */}
      <section className="mkt-hero" data-testid="mkt-about-hero" style={{ minHeight: "auto", paddingTop: 96, paddingBottom: 40 }}>
        <div className="mkt-eyebrow">About</div>
        <h1 className="mkt-h-xl" style={{ marginTop: 26, maxWidth: "22ch" }}>
          The entire sovereign intelligence stack, owned in-country.
        </h1>
        <p className="mkt-body mkt-body-lg" style={{ marginTop: 32, maxWidth: "60ch" }}>
          Crowther builds and owns the intelligence stack that regulated enterprises run on, so that their intelligence, their data, and their infrastructure are owned where they operate rather than rented from abroad.
        </p>
        <p className="mkt-serif mkt-italic" style={{ marginTop: 28, fontSize: 22, color: "var(--slate)" }}>
          Human Insight, Amplified.
        </p>
      </section>

      {/* What we build */}
      <TwoCol
        id="mkt-about-build"
        label="What we build"
        title="We build the layers between frontier intelligence and the enterprise."
      >
        <div className="mkt-stack-24">
          <p className="mkt-body">
            Frontier models will keep improving, and they are an input we harness rather than a product we ship. The value that reaches an institution is what sits on top: specialist intelligence, trained locally, running on infrastructure the institution owns, resolving the records inside its own systems into the entities that actually matter to it.
          </p>
          <p className="mkt-body">
            We own the chain from the metal upward. In-country compute is the ground. Crowther Cloud is the sovereign substrate. Atom is the specialist model family. Foundation is the governed platform that resolves and reasons. And across all of it, one measure holds: every deployment must elevate more human work than it displaces. That is a design constraint, not an aspiration.
          </p>
        </div>
      </TwoCol>

      {/* Who we serve */}
      <section className="mkt-band" data-testid="mkt-about-serve">
        <div className="mkt-shell">
          <div className="mkt-label" style={{ marginBottom: 20 }}>Who we serve</div>
          <h2 className="mkt-h-m" style={{ maxWidth: "24ch" }}>We serve the institutions that answer to a regulator.</h2>
          <p className="mkt-body" style={{ marginTop: 24, maxWidth: "72ch" }}>
            Banks and payments infrastructure. Insurers and health systems. Telecom operators and national utilities. Ministries and central banks. The organisations for whom the decision to deploy AI is not a product decision but a governance decision, and for whom the price of getting it wrong is measured in regulator action, not in NPS.
          </p>
        </div>
      </section>

      {/* Why we exist */}
      <TwoCol
        id="mkt-about-why"
        label="Why we exist"
        title="Sovereignty is becoming the price of participation."
      >
        <div className="mkt-stack-24">
          <p className="mkt-body">
            The world&rsquo;s leading models sit inside a small number of countries, and everyone else has been asked to send their data there to use them. That has never been a stable arrangement for a regulated institution, and it has stopped being politically stable for the countries hosting the data as well. Data residency and localisation rules were in force across more than one hundred and fifty countries by the end of 2024, and the trajectory is towards more, not fewer.
          </p>
          <p className="mkt-body">
            The counter-model is straightforward. The intelligence and the data both need to live where the institution operates, under the laws it is answerable to, and under owners inside the jurisdiction. That is precisely what we build. Intelligence and data that reside in-country, under local ownership and local authority.
          </p>
        </div>
      </TwoCol>

      {/* What we want to achieve */}
      <TwoCol
        id="mkt-about-goals"
        label="What we want to achieve"
        title="Access, knowledge, and sovereignty, in that order."
      >
        <div>
          <div className="mkt-num-row">
            <div className="num">01 Access</div>
            <div className="body">
              <p>Make frontier intelligence usable by the institutions and countries that were structurally locked out of it. Not through a proxy account with a foreign platform, but through infrastructure that runs where the institution operates, owned by owners inside its jurisdiction.</p>
            </div>
          </div>
          <div className="mkt-num-row">
            <div className="num">02 Knowledge</div>
            <div className="body">
              <p>Turn what institutions already know into an asset they can reason over. The records, the filings, the transactions, the case notes, resolved into the entities behind them and connected into a knowledge substrate the institution owns.</p>
            </div>
          </div>
          <div className="mkt-num-row">
            <div className="num">03 Sovereignty</div>
            <div className="body">
              <p>Keep the intelligence and the data on ground the institution controls, forever. No retroactive vendor lock-in, no jurisdictional exposure through the back door, no policy risk from a decision taken in someone else&rsquo;s capital.</p>
            </div>
          </div>
          <p className="mkt-body" style={{ marginTop: 32 }}>
            The ambition is not that Crowther becomes a large company. The ambition is that the institutions we work inside become materially harder to displace, and that the countries they operate in stop having to depend on infrastructure elsewhere to run their own intelligence.
          </p>
          <p className="mkt-serif-lead" style={{ marginTop: 24 }}>
            Access opens it. Knowledge fills it. Sovereignty keeps it.
          </p>
        </div>
      </TwoCol>

      {/* The name */}
      <TwoCol
        id="mkt-about-name"
        label="The name"
        title="Crowther, read across its letters, is four commitments."
      >
        <p className="mkt-body" style={{ marginBottom: 24 }}>
          The name reads across its letters as the four commitments the company makes, and they are the same four commitments the deployment makes.
        </p>
        {NAME_ROWS.map((r) => (
          <div key={r.code} className="mkt-row" data-testid={`mkt-name-${r.code.replace(".", "").toLowerCase()}`} style={{ gridTemplateColumns: "80px 0.9fr 1.4fr" }}>
            <div className="mkt-row-name" style={{ fontSize: 22 }}>{r.code}</div>
            <div className="mkt-row-name" style={{ fontSize: 22 }}>{r.name}</div>
            <div className="mkt-row-desc">{r.desc}</div>
          </div>
        ))}
      </TwoCol>

      {/* Values */}
      <section className="mkt-band" data-testid="mkt-about-values">
        <div className="mkt-shell">
          <div className="mkt-label" style={{ marginBottom: 20 }}>Our values</div>
          <div className="mkt-split" style={{ padding: "0" }}>
            <div className="mkt-split-title-col">
              <h2 className="mkt-h-l">CORE.</h2>
              <p className="mkt-body mkt-body-lg mkt-split-lede">How the people inside the company work.</p>
            </div>
            <div>
              {VALUES.map((v) => (
                <div key={v.name} className="mkt-row" data-testid={`mkt-value-${v.name.replace(".", "").toLowerCase()}`}>
                  <div className="mkt-row-name">{v.name}</div>
                  <div className="mkt-row-desc">{v.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Elevation principle */}
      <TwoCol
        id="mkt-about-jq"
        label="The Elevation Principle"
        title="JQ greater than 1.0."
      >
        <div className="mkt-stack-24">
          <p className="mkt-body">
            Every Crowther deployment carries one measured constraint. The Job Quotient, JQ, expresses the ratio of work elevated to work displaced across the deployment. Where JQ is above 1.0, human work has been lifted, at scale, by the deployment. Where it is below 1.0, work has been hollowed out and the design has failed the constraint.
          </p>
          <p className="mkt-body">
            Realloc measures JQ at the level of tasks rather than job titles, and returns clear verdicts on what to retain, retrain, or redeploy. Deployments that fail the constraint are re-scoped. The rule is asserted publicly and enforced privately.
          </p>
        </div>
      </TwoCol>

      {/* What we believe */}
      <section className="mkt-band" data-testid="mkt-about-belief">
        <div className="mkt-shell">
          <div className="mkt-label" style={{ marginBottom: 20 }}>What we believe</div>
          <h2 className="mkt-h-m" style={{ maxWidth: "26ch" }}>Human and machine are the fig and the fig wasp.</h2>
          <p className="mkt-body" style={{ marginTop: 24, maxWidth: "72ch" }}>
            The fig cannot ripen without the wasp, and the wasp cannot reproduce without the fig. Each depends on what the other cannot do alone. Intelligence, at its best, is that. Human judgement carries the meaning; machine intelligence carries the range. Crowther is built for that pairing, deliberately. Human Insight, Amplified.
          </p>
        </div>
      </section>

      {/* Our mark */}
      <TwoCol
        id="mkt-about-mark"
        label="Our mark"
        title="The fig is a partnership, and so is the logo."
      >
        <div className="mkt-stack-24">
          <p className="mkt-body">
            The fig blooms inward, not outward. The flowers sit inside the fruit, hidden from view, and the wasp enters to complete the exchange. It is a form that looks quiet from the outside because the work is happening on the inside. That is the register the mark carries.
          </p>
          <p className="mkt-body">
            The nucleus at the centre of the mark is the point of exchange, the moment of resolution where the four commitments meet. The leaves are held still around it. There is no motion in the wordmark; the mark is a signature, not an animation.
          </p>
          <div style={{ display: "flex", gap: 40, alignItems: "center", flexWrap: "wrap", marginTop: 24 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <img src="/logos/mark-black.png" alt="Crowther mark" style={{ width: 84, height: 84 }} />
              <div>
                <div className="mkt-serif" style={{ fontSize: 22 }}>Crowther</div>
                <div className="mkt-meta">Four leaves. One nucleus. Held still.</div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <img src="/logos/mark-seafoam.png" alt="Atom mark" style={{ width: 84, height: 84, animation: "mkt-spin 26s linear infinite" }} />
              <div>
                <div className="mkt-serif" style={{ fontSize: 22 }}>Atom</div>
                <div className="mkt-meta">An atom wrapped in fig leaves.</div>
              </div>
            </div>
          </div>
          <p className="mkt-body">
            The Crowther and Atom marks are the same family. Same leaves, same nucleus, one held still, the other in motion. Same lineage, one commitment.
          </p>
        </div>
      </TwoCol>

      {/* Close */}
      <section className="mkt-band" data-testid="mkt-about-close">
        <div className="mkt-shell">
          <h2 className="mkt-h-m" style={{ maxWidth: "24ch" }}>The next battleground is deployment, not demonstration.</h2>
          <p className="mkt-body" style={{ marginTop: 20, maxWidth: "60ch" }}>
            Have a question that is not covered here? Reach out and we will respond within one business day.
          </p>
          <div style={{ marginTop: 32, display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
            <Link to="/enterprise/contact" className="mkt-btn mkt-btn-link" data-testid="mkt-about-contact">
              Contact us
            </Link>
            <Link to="/plan" className="mkt-btn mkt-btn-primary" data-testid="mkt-about-cta">
              Get Your Firm&rsquo;s Free Customised AI Roadmap
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
